import React from "react";
import type {
  FmMapFilterValues,
  FmMapWorkspaceDescriptor,
} from "./FmMapWorkspaceRoot.types";
export type FmMapEmitFilter = (
  filterId: string,
  values: readonly string[],
  source: "panel" | "undo",
) => void;

/** Owns the renderer's complete semantic filter snapshot and event emission. */
export function useMapWorkspaceFilters(descriptor: FmMapWorkspaceDescriptor): {
  emitFilter: FmMapEmitFilter;
  filterValues: FmMapFilterValues;
} {
  const filterSelectionKey = JSON.stringify(
    descriptor.filters.map((filter) => [filter.id, filter.selectedValues]),
  );
  const [filterValues, setFilterValues] = React.useState<FmMapFilterValues>(
    () =>
      Object.fromEntries(
        descriptor.filters.map((filter) => [filter.id, filter.selectedValues]),
      ),
  );
  const filterValuesRef = React.useRef(filterValues);
  const canEnterRefine = descriptor.story.allowedTransitions.includes("refine");
  const { mode, onEvent } = descriptor;

  React.useEffect(() => {
    const nextValues = Object.fromEntries(
      descriptor.filters.map((filter) => [filter.id, filter.selectedValues]),
    );
    filterValuesRef.current = nextValues;
    setFilterValues(nextValues);
    // Fresh descriptor arrays synchronize through their serialized semantic values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    descriptor.id,
    descriptor.source.id,
    descriptor.viewport.revision,
    filterSelectionKey,
  ]);

  const emitFilter = React.useCallback<FmMapEmitFilter>(
    (filterId, values, source): void => {
      if (
        values.length > 0 &&
        mode !== "refine" &&
        !canEnterRefine
      ) {
        return;
      }
      const activeFilters = {
        ...filterValuesRef.current,
        [filterId]: values,
      };
      filterValuesRef.current = activeFilters;
      setFilterValues(activeFilters);
      onEvent?.({
        kind: "filter-change",
        filterId,
        values,
        activeFilters,
        source,
      });
    },
    [canEnterRefine, mode, onEvent],
  );

  return { emitFilter, filterValues };
}
